"use client";
import React, { useState, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import IMAGES from "@/data/assetData";
import Link from "next/link";

const LoginModal = dynamic(() => import("@/components/Login"), { ssr: false });
const RegisterModal = dynamic(() => import("@/components/Register"), { ssr: false });
import { BASE_URL_CITY } from "@/data/assetData";
import { useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { getUserLogout } from "@/redux/user/userSlice";
import { resetAnonymousId } from "@/utils/anonymousId";
import { getApiToken } from "@/redux/config/configSlice";
import { initiateToken } from "@/utils/services/tokenService";
import Image from "next/image";


const ProfileMenu = ({ refetchCart }) => {
  const showHideRef = useRef();
  const dispatch = useDispatch();
  const router = useRouter();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [loginType, setLoginType] = useState("mobile");
  const { isAuthenticated } = useSelector((state) => state.user);

  const handleLogout = async () => {
    dispatch(getUserLogout());
    resetAnonymousId();
    await handleConfig();
    router.push("/");
  };

  const handleConfig = async () => {
    // Actually END the session: clear the user JWT immediately, then swap in a
    // fresh guest token. Before this, the minted guest token was discarded and
    // the user JWT (which never expires) kept riding every request after
    // "logout" — so the next person on a shared device had their searches
    // attributed to the previous account, and their next login handed them the
    // previous account's cart.
    dispatch(getApiToken(""));
    const newToken = await initiateToken();
    if (newToken) {
      dispatch(getApiToken(newToken));
      refetchCart();
    }
  };

  useEffect(() => {
    const closeDropdown = (e) => {
      if (showHideRef.current && !showHideRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.body.addEventListener("click", closeDropdown);
    return () => document.body.removeEventListener("click", closeDropdown);
  }, []);

  return (
    <>
      <div className="relative cursor-pointer h-[46px] w-[84px] hidden lg:block ml-6" ref={showHideRef}>
        <button
          type="button"
          className="relative w-full h-full"
          onClick={() => setShowDropdown(!showDropdown)}
          aria-expanded={showDropdown}
          aria-haspopup="true"
          aria-label="Account menu"
        >
          <Image
            src={IMAGES.profile_icon}
            fill
            sizes="84px"
            alt="Profile"
            aria-hidden="true"
          />
        </button>
        {showDropdown && (
          <div
            className="absolute top-full right-0 bg-white text-black py-6 flex flex-col min-w-[220px] rounded-2xl z-[1] font-normal shadow-[0_10px_40px_rgba(0,0,0,0.1),0_0_0_1px_rgba(255,90,95,0.05)]"
            role="menu"
          >
            {!isAuthenticated && (
              <button
                type="button"
                role="menuitem"
                className="px-6 py-3 cursor-pointer text-sm transition-colors duration-200 bg-transparent border-none font-inherit text-inherit text-left w-full hover:text-textPrimary hover:bg-redSurface-50"
                onClick={() => setIsLoginModalOpen(true)}
              >
                Login
              </button>
            )}

            {isAuthenticated && (
              <span
                role="menuitem"
                className="px-6 py-3 cursor-pointer text-sm transition-colors duration-200 text-inherit text-left w-full hover:text-textPrimary hover:bg-redSurface-50"
              >
                <Link href={`${BASE_URL_CITY}/user/dashboard`}>Account</Link>
              </span>
            )}
            <span
              role="menuitem"
              className="px-6 py-3 cursor-pointer text-sm transition-colors duration-200 text-inherit text-left w-full hover:text-textPrimary hover:bg-redSurface-50"
            >
              <Link href="/contact">Help Centre</Link>
            </span>
            {isAuthenticated && (
              <button
                type="button"
                role="menuitem"
                className="px-6 py-3 cursor-pointer text-sm transition-colors duration-200 bg-transparent border-none font-inherit text-inherit text-left w-full hover:text-textPrimary hover:bg-redSurface-50"
                onClick={() => handleLogout()}
              >
                Log Out
              </button>
            )}
          </div>
        )}
      </div>
      {isLoginModalOpen && (
        <LoginModal
        setIsLoginModalOpen={setIsLoginModalOpen}
        setIsRegisterModalOpen={setIsRegisterModalOpen}
        setLoginType={setLoginType}
        location={`${BASE_URL_CITY}/user/dashboard`}
      />
      )}
      {isRegisterModalOpen && (
       <RegisterModal
       setIsRegisterModalOpen={setIsRegisterModalOpen}
       loginType={loginType}
       location={`${BASE_URL_CITY}/user/dashboard`}
     />
      )}
    </>
  );
};

export default ProfileMenu;
